import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bell, AlertTriangle, Trophy, Sparkles, X, Inbox, Users } from 'lucide-react';
import { cn } from '../lib/utils';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const TYPE_ICON = {
  overdue_followup: AlertTriangle,
  won: Trophy,
  new_lead: Sparkles,
  deal_assigned: Users,
};
const TYPE_COLOR = {
  overdue_followup: 'text-rose-500 dark:text-rose-400',
  won: 'text-emerald-500 dark:text-emerald-400',
  new_lead: 'text-blue-500 dark:text-blue-400',
  deal_assigned: 'text-violet-500 dark:text-violet-400',
};

const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    return `${diffDay}d ago`;
  } catch (e) { return ''; }
};

const NotificationsBell = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const intervalRef = useRef(null);

  const fetchNotifications = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch(`${backendUrl}/api/nurture/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const data = await r.json();
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  // poll every 60s, pause when tab hidden
  useEffect(() => {
    if (!token) return;
    fetchNotifications();
    const start = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        if (!document.hidden) fetchNotifications();
      }, 60000);
    };
    start();
    const onVisibility = () => {
      if (!document.hidden) fetchNotifications();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [token]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleItemClick = (item) => {
    setOpen(false);
    if (item.type === 'deal_assigned') {
      if (item.notification_id) fetch(`${backendUrl}/api/onboarding/team/my-notifications/${item.notification_id}/read`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      navigate(`/onboarding/${item.application_id}`);
      return;
    }
    if (item.lead_id) {
      navigate(`/lead-nurture/leads/${item.lead_id}`);
    } else {
      navigate('/lead-nurture');
    }
  };

  if (!token) return null;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-800/70 transition-all"
        aria-label="Notifications"
        title={count > 0 ? `${count} notification${count === 1 ? '' : 's'}` : 'Notifications'}
        data-testid="notifications-bell-btn"
      >
        <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center shadow-lg ring-2 ring-white dark:ring-slate-950 animate-pulse"
            data-testid="notifications-badge"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 max-h-[80vh] bg-slate-900 dark:bg-slate-900 border border-slate-700 dark:border-slate-700 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden"
          data-testid="notifications-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Notifications
              {count > 0 && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300">{count}</span>
              )}
            </h3>
            <button onClick={() => setOpen(false)} aria-label="Close" className="text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="py-8 text-center text-slate-500 text-sm">Loading…</div>
            ) : items.length === 0 ? (
              <div className="py-10 px-6 text-center text-slate-400">
                <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">All caught up!</p>
                <p className="text-xs text-slate-500 mt-1">No overdue tasks, new leads or wins right now.</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-800">
                {items.map(item => {
                  const Icon = TYPE_ICON[item.type] || Bell;
                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => handleItemClick(item)}
                        className="w-full text-left px-4 py-3 hover:bg-slate-800/70 transition-colors group"
                        data-testid={`notification-item-${item.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <Icon className={cn('w-4 h-4 flex-shrink-0 mt-0.5', TYPE_COLOR[item.type])} />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-white group-hover:text-emerald-300 transition-colors truncate">
                              {item.title}
                            </p>
                            <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{item.body}</p>
                            <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-wide">
                              {formatRelative(item.created_at)}
                            </p>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="px-4 py-2 border-t border-slate-700 flex items-center justify-between">
            <button
              onClick={fetchNotifications}
              className="text-xs text-slate-400 hover:text-emerald-300 transition-colors"
            >
              Refresh
            </button>
            <button
              onClick={() => { setOpen(false); navigate('/lead-nurture'); }}
              className="text-xs text-emerald-400 hover:text-emerald-300 font-medium"
            >
              Open Lead Nurture →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
